import DetailsCard from "@/components/DetailsCard";
import type { ListItemProps } from "@/components/ListItem/ListItem";
import SectionHeading from "@/components/SectionHeading";

export type FlexCallDetailsCall = {
  transcriptSummary?: string | null;
  transcriptTopic?: string | null;
  transcriptSubtopic?: string | null;
  transcriptTopicConfidence?: number | null;
  sentimentLabel?: string | null;
  sentimentSource?: string | null;
  workerName?: string | null;
  direction?: string | null;
  durationSeconds?: number | null;
  queueTimeSeconds?: number | null;
  recordingUrl?: string | null;
  firstContactOnIssue?: string | null;
  failedPriorContact?: boolean | null;
  failedPriorContactDetail?: string | null;
  callerState?: string | null;
  issueCount?: number | null;
};

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const FIRST_CONTACT_LABELS: Record<string, string> = {
  first_time: "First-time contact",
  repeat: "Repeat contact"
};

const CALLER_STATE_LABELS: Record<string, string> = {
  stuck_needs_action: "Needs action",
  confused_or_mistaken: "Confused or mistaken",
  needs_information: "Needs information"
};

const SENTIMENT_SOURCE_LABELS: Record<string, string> = {
  twilio: "Twilio"
};

const formatDuration = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  if (hours === 0) return `${minutes}:${String(remainder).padStart(2, "0")}`;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
};

type Props = {
  call: FlexCallDetailsCall;
  /** Edit affordance for the summary card. Rendered as the card's edit button when provided. */
  onEditSummary?: () => void;
};

export default function FlexCallDetails({ call, onEditSummary }: Props) {
  const summary = call.transcriptSummary?.trim() || null;
  const topic = call.transcriptTopic?.trim() || null;
  const subtopic = call.transcriptSubtopic?.trim() || null;
  // Backend sends a 0-1 fraction (TranscriptAnalysisSchema clamps 0-1).
  const confidence =
    typeof call.transcriptTopicConfidence === "number"
      ? Math.min(100, Math.max(0, Math.round(call.transcriptTopicConfidence * 100)))
      : null;

  // Twilio-only: never render inferred or unknown-source sentiment, even if
  // the backend still serves a legacy label.
  const sentiment =
    call.sentimentSource === "twilio" && call.sentimentLabel?.trim() ? capitalize(call.sentimentLabel.trim()) : null;
  const sentimentSource = call.sentimentSource ? (SENTIMENT_SOURCE_LABELS[call.sentimentSource] ?? null) : null;

  const callerParts = [
    call.firstContactOnIssue ? (FIRST_CONTACT_LABELS[call.firstContactOnIssue] ?? null) : null,
    call.callerState ? (CALLER_STATE_LABELS[call.callerState] ?? null) : null,
    typeof call.issueCount === "number" ? `${call.issueCount} ${call.issueCount === 1 ? "issue" : "issues"}` : null,
    call.failedPriorContact
      ? `Mentioned a failed prior contact${call.failedPriorContactDetail?.trim() ? ` — ${call.failedPriorContactDetail.trim()}` : ""}`
      : null
  ].filter((part): part is string => !!part);

  const callParts = [
    call.direction?.trim() ? capitalize(call.direction.trim()) : null,
    typeof call.durationSeconds === "number" ? formatDuration(call.durationSeconds) : null,
    call.workerName?.trim() ? call.workerName.trim() : null,
    typeof call.queueTimeSeconds === "number" ? `Waited ${call.queueTimeSeconds}s in queue` : null
  ].filter((part): part is string => !!part);

  const factOptions = (
    [
      topic && {
        overline: "Topic",
        headline: subtopic ? `${topic} › ${subtopic}` : topic,
        ...(confidence !== null ? { trailingSupportingText: `${confidence}% confident` } : {})
      },
      sentiment && {
        overline: "Sentiment",
        headline: sentimentSource ? `${sentiment} · ${sentimentSource}` : sentiment
      },
      callerParts.length > 0 && {
        overline: "Caller",
        headline: callerParts.join(" · ")
      },
      callParts.length > 0 && {
        overline: "Call",
        headline: callParts.join(" · ")
      },
      call.recordingUrl?.trim() && {
        overline: "Recording",
        headline: (
          <a href={call.recordingUrl.trim()} target="_blank" rel="noopener noreferrer">
            Open in Twilio Console
          </a>
        )
      }
    ] as (ListItemProps | false | "" | null | undefined)[]
  ).filter((option): option is ListItemProps => !!option);

  if (!summary && factOptions.length === 0) return null;

  const summaryOptions: ListItemProps[] = summary ? [{ headline: summary, longContent: true }] : [];

  const summaryCard = summary ? (
    <DetailsCard testId="flex-call-details-summary" heading="Summary" onEdit={onEditSummary} options={summaryOptions} />
  ) : null;

  return (
    <>
      <SectionHeading heading="Call Details" body="AI summary, classification, and call facts for this phone call." />
      {summary && factOptions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {summaryCard}
          <DetailsCard testId="flex-call-details" options={factOptions} />
        </div>
      ) : (
        (summaryCard ?? <DetailsCard testId="flex-call-details" options={factOptions} />)
      )}
    </>
  );
}
